"use client";

import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis, Cell } from "recharts";
import { ChartCard } from "./chart-card";
import type { BreakdownEntry } from "@/types/analytics";

interface RouteErrorChartProps {
  entries: BreakdownEntry[];
  activeRoutes: string[] | undefined;
  isLoading: boolean;
  onSelectRoute: (route: string) => void;
}

export function RouteErrorChart({ entries, activeRoutes, isLoading, onSelectRoute }: RouteErrorChartProps) {
  const activeSet = new Set(activeRoutes ?? []);

  return (
    <ChartCard
      title="Top routes by error count"
      description="Click a bar to filter the dashboard to that route."
      isLoading={isLoading}
      isEmpty={!isLoading && entries.length === 0}
      emptyMessage="No route errors in the current results."
    >
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={entries} layout="vertical" margin={{ left: 8, right: 16 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" horizontal={false} />
          <XAxis type="number" tick={{ fontSize: 11, fill: "var(--muted-foreground)" }} axisLine={false} tickLine={false} allowDecimals={false} />
          <YAxis
            dataKey="key"
            type="category"
            width={140}
            tick={{ fontSize: 11, fill: "var(--muted-foreground)" }}
            axisLine={false}
            tickLine={false}
          />
          <Tooltip
            cursor={{ fill: "var(--accent)" }}
            contentStyle={{
              background: "var(--popover)",
              color: "var(--popover-foreground)",
              border: "1px solid var(--border)",
              borderRadius: 8,
              fontSize: 12,
            }}
          />
          <Bar
            dataKey="totalErrors"
            name="Errors"
            radius={[0, 4, 4, 0]}
            cursor="pointer"
            onClick={(entry) => {
              const key = (entry as unknown as BreakdownEntry).key;
              if (key) onSelectRoute(key);
            }}
          >
            {entries.map((entry) => (
              <Cell
                key={entry.key}
                fill="var(--chart-1)"
                fillOpacity={activeSet.size === 0 || activeSet.has(entry.key) ? 1 : 0.35}
              />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </ChartCard>
  );
}
